"use client"

import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkHtml from 'remark-html'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism'
import Link from 'next/link'
import Image from 'next/image'

interface MarkdownRendererProps {
  content: string
  className?: string
}

export function MarkdownRenderer({ content, className = "" }: MarkdownRendererProps) {
  // Check if content is HTML (starts with <) or markdown
  const isHtml = content.trim().startsWith('<')

  if (isHtml) {
    // Render HTML content directly with proper styling
    return (
      <div className={`prose prose-lg max-w-none ${className}`}>
        <div
          className="rich-text-content"
          dangerouslySetInnerHTML={{ __html: content }}
          style={{
            '--tw-prose-links': 'var(--home-navy, #102338)',
            '--tw-prose-underline': 'underline'
          } as React.CSSProperties}
        />
        <style jsx global>{`
          .rich-text-content h1 {
            font-size: 2.25rem;
            font-weight: 700;
            color: var(--home-navy, #102338);
            margin-bottom: 1.5rem;
            margin-top: 2rem;
            border-bottom: 1px solid color-mix(in oklch, var(--home-navy, #102338) 14%, transparent);
            padding-bottom: 0.75rem;
          }
          .rich-text-content h1:first-child {
            margin-top: 0;
          }
          .rich-text-content h2 {
            font-size: 1.875rem;
            font-weight: 600;
            color: var(--home-navy, #102338);
            margin-bottom: 1rem;
            margin-top: 2rem;
          }
          .rich-text-content h2:first-child {
            margin-top: 0;
          }
          .rich-text-content h3 {
            font-size: 1.5rem;
            font-weight: 600;
            color: var(--home-navy, #102338);
            margin-bottom: 0.75rem;
            margin-top: 1.5rem;
          }
          .rich-text-content h3:first-child {
            margin-top: 0;
          }
          .rich-text-content h4 {
            font-size: 1.25rem;
            font-weight: 600;
            color: var(--home-navy, #102338);
            margin-bottom: 0.5rem;
            margin-top: 1rem;
          }
          .rich-text-content h5 {
            font-size: 1.125rem;
            font-weight: 600;
            color: var(--home-navy, #102338);
            margin-bottom: 0.5rem;
            margin-top: 1rem;
          }
          .rich-text-content h6 {
            font-size: 1rem;
            font-weight: 600;
            color: var(--home-navy, #102338);
            margin-bottom: 0.5rem;
            margin-top: 1rem;
          }
          .rich-text-content p {
            color: color-mix(in oklch, var(--home-ink, #102338) 68%, var(--home-paper, #f3efe6));
            line-height: 1.625;
            margin-bottom: 1rem;
          }
          .rich-text-content ul {
            list-style-type: disc;
            list-style-position: inside;
            margin-bottom: 1rem;
            color: color-mix(in oklch, var(--home-ink, #102338) 68%, var(--home-paper, #f3efe6));
          }
          .rich-text-content ol {
            list-style-type: decimal;
            list-style-position: inside;
            margin-bottom: 1rem;
            color: color-mix(in oklch, var(--home-ink, #102338) 68%, var(--home-paper, #f3efe6));
          }
          .rich-text-content li {
            color: color-mix(in oklch, var(--home-ink, #102338) 68%, var(--home-paper, #f3efe6));
            line-height: 1.625;
          }
          .rich-text-content blockquote {
            border-left: 4px solid var(--home-gold, #c4a056);
            padding-left: 1rem;
            padding-top: 0.5rem;
            padding-bottom: 0.5rem;
            margin-bottom: 1rem;
            background-color: color-mix(in oklch, var(--home-gold, #c4a056) 8%, var(--home-paper, #f3efe6));
            color: color-mix(in oklch, var(--home-ink, #102338) 62%, var(--home-paper, #f3efe6));
            font-style: italic;
          }
          .rich-text-content code {
            background-color: color-mix(in oklch, var(--home-ink, #102338) 8%, var(--home-paper, #f3efe6));
            color: var(--home-navy, #102338);
            padding: 0.125rem 0.375rem;
            border-radius: 0.25rem;
            font-size: 0.875rem;
            font-family: ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace;
          }
          .rich-text-content pre {
            background-color: #111827;
            color: #f9fafb;
            padding: 1rem;
            border-radius: 0.5rem;
            overflow-x: auto;
            margin-bottom: 1rem;
          }
          .rich-text-content a {
            color: var(--home-navy, #102338) !important;
            text-decoration: underline !important;
          }
          .rich-text-content a:hover {
            color: var(--home-gold, #c4a056) !important;
          }
          .rich-text-content strong {
            font-weight: 600;
            color: var(--home-navy, #102338);
          }
          .rich-text-content em {
            font-style: italic;
            color: color-mix(in oklch, var(--home-ink, #102338) 68%, var(--home-paper, #f3efe6));
          }
          .rich-text-content img {
            margin: 1.5rem 0;
            border-radius: 0.5rem;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            width: 100%;
            height: auto;
          }
          .rich-text-content table {
            min-width: 100%;
            border: 1px solid color-mix(in oklch, var(--home-navy, #102338) 14%, transparent);
            border-radius: 0.5rem;
            margin-bottom: 1rem;
            overflow-x: auto;
            display: block;
          }
          .rich-text-content thead {
            background-color: color-mix(in oklch, var(--home-ink, #102338) 6%, var(--home-paper, #f3efe6));
          }
          .rich-text-content tbody {
            border-top: 1px solid color-mix(in oklch, var(--home-navy, #102338) 14%, transparent);
          }
          .rich-text-content tr {
            border-bottom: 1px solid color-mix(in oklch, var(--home-navy, #102338) 14%, transparent);
          }
          .rich-text-content th {
            padding: 0.5rem 1rem;
            text-align: left;
            font-size: 0.875rem;
            font-weight: 600;
            color: var(--home-navy, #102338);
            border-bottom: 1px solid color-mix(in oklch, var(--home-navy, #102338) 14%, transparent);
          }
          .rich-text-content td {
            padding: 0.5rem 1rem;
            font-size: 0.875rem;
            color: color-mix(in oklch, var(--home-ink, #102338) 68%, var(--home-paper, #f3efe6));
            border-bottom: 1px solid color-mix(in oklch, var(--home-navy, #102338) 14%, transparent);
          }
          .rich-text-content hr {
            margin: 2rem 0;
            border-color: color-mix(in oklch, var(--home-navy, #102338) 14%, transparent);
          }

          .rich-text-content iframe {
            margin: 1.5rem 0;
            border-radius: 0.5rem;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            max-width: 100%;
            height: auto;
            min-height: 300px;
          }

          .rich-text-content iframe[src*="youtube"] {
            aspect-ratio: 16/9;
            width: 100%;
            height: auto;
          }

          .rich-text-content iframe[src*="google.com/maps"] {
            aspect-ratio: 4/3;
            width: 100%;
            height: auto;
            min-height: 400px;
          }

          .rich-text-content iframe[src*="vimeo"] {
            aspect-ratio: 16/9;
            width: 100%;
            height: auto;
          }

          .rich-text-content iframe[src*="spotify"] {
            width: 100%;
            height: 380px;
          }
        `}</style>
      </div>
    )
  }

  return (
    <div className={`prose prose-lg max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkHtml]}
        components={{
          h1: ({ children }) => (
            <h1 className="font-heading text-[clamp(2rem,4vw,2.75rem)] font-medium leading-tight tracking-[-0.03em] text-[var(--home-navy,#102338)] mb-6 mt-8 first:mt-0 border-b border-[color-mix(in_oklch,var(--home-navy,#102338)14%,transparent)] pb-3">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="font-heading text-[clamp(1.6rem,3vw,2.25rem)] font-medium leading-snug tracking-[-0.02em] text-[var(--home-navy,#102338)] mb-4 mt-8 first:mt-0">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="font-heading text-2xl font-medium tracking-[-0.02em] text-[var(--home-navy,#102338)] mb-3 mt-6 first:mt-0">
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className="text-xl font-medium text-[var(--home-navy,#102338)] mb-2 mt-4 first:mt-0">
              {children}
            </h4>
          ),
          h5: ({ children }) => (
            <h5 className="text-lg font-medium text-[var(--home-navy,#102338)] mb-2 mt-4 first:mt-0">
              {children}
            </h5>
          ),
          h6: ({ children }) => (
            <h6 className="text-base font-semibold text-[var(--home-navy,#102338)] mb-2 mt-4 first:mt-0">
              {children}
            </h6>
          ),
          p: ({ children }) => (
            <p className="leading-relaxed mb-4 text-[color-mix(in_oklch,var(--home-ink,#102338)_68%,var(--home-paper,#f3efe6))]">
              {children}
            </p>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-inside mb-4 space-y-1 text-[color-mix(in_oklch,var(--home-ink,#102338)_68%,var(--home-paper,#f3efe6))]">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-inside mb-4 space-y-1 text-[color-mix(in_oklch,var(--home-ink,#102338)_68%,var(--home-paper,#f3efe6))]">
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="leading-relaxed text-[color-mix(in_oklch,var(--home-ink,#102338)_68%,var(--home-paper,#f3efe6))]">
              {children}
            </li>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-[var(--home-gold,#c4a056)] pl-4 py-2 mb-4 bg-[color-mix(in_oklch,var(--home-gold,#c4a056)8%,var(--home-paper,#f3efe6))] text-[color-mix(in_oklch,var(--home-ink,#102338)_62%,var(--home-paper,#f3efe6))] italic">
              {children}
            </blockquote>
          ),
          code: ({ children, className }) => {
            const match = /language-(\w+)/.exec(className || '')
            return match ? (
              <SyntaxHighlighter
                style={tomorrow}
                language={match[1]}
                PreTag="div"
                className="rounded-lg"
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            ) : (
              <code className="bg-[color-mix(in_oklch,var(--home-ink,#102338)8%,var(--home-paper,#f3efe6))] text-[var(--home-navy,#102338)] px-1.5 py-0.5 rounded text-sm font-mono">
                {children}
              </code>
            )
          },
          pre: ({ children }) => (
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto mb-4">
              {children}
            </pre>
          ),
          a: ({ href, children }) => {
            if (href?.startsWith('http')) {
              return (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[var(--home-navy,#102338)] hover:text-[var(--home-gold,#c4a056)] underline transition-colors duration-200"
                >
                  {children}
                </a>
              )
            }
            return (
              <Link href={href || '#'} className="text-[var(--home-navy,#102338)] hover:text-[var(--home-gold,#c4a056)] underline transition-colors duration-200">
                {children}
              </Link>
            )
          },
          img: ({ src, alt }) => (
            <div className="my-6">
              <Image
                src={typeof src === 'string' ? src : ''}
                alt={alt || ''}
                width={800}
                height={400}
                className="rounded-lg shadow-md w-full h-auto"
              />
            </div>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full border border-[color-mix(in_oklch,var(--home-navy,#102338)14%,transparent)] rounded-lg">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-[color-mix(in_oklch,var(--home-ink,#102338)6%,var(--home-paper,#f3efe6))]">
              {children}
            </thead>
          ),
          tbody: ({ children }) => (
            <tbody className="divide-y divide-[color-mix(in_oklch,var(--home-navy,#102338)14%,transparent)]">
              {children}
            </tbody>
          ),
          tr: ({ children }) => (
            <tr>
              {children}
            </tr>
          ),
          th: ({ children }) => (
            <th className="px-4 py-2 text-left text-sm font-semibold text-[var(--home-navy,#102338)] border-b border-[color-mix(in_oklch,var(--home-navy,#102338)14%,transparent)]">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-4 py-2 text-sm text-[color-mix(in_oklch,var(--home-ink,#102338)_68%,var(--home-paper,#f3efe6))] border-b border-[color-mix(in_oklch,var(--home-navy,#102338)14%,transparent)]">
              {children}
            </td>
          ),
          hr: () => (
            <hr className="my-8 border-[color-mix(in_oklch,var(--home-navy,#102338)14%,transparent)]" />
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-[var(--home-navy,#102338)]">
              {children}
            </strong>
          ),
          em: ({ children }) => (
            <em className="italic text-[color-mix(in_oklch,var(--home-ink,#102338)_68%,var(--home-paper,#f3efe6))]">
              {children}
            </em>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}